import type {
  CanvasCard,
  CardType,
  ChartCardConfig,
  ChartType,
  KpiCardConfig,
  SizePreset,
  SummaryCardConfig,
  TableCardConfig
} from "store/types";
import { DEFAULT_GRID_H, PRESET_GRID_W } from "store/types/reports/report-generator";

import { gridWToWidthPercent } from "./gridUtils";

export const DEFAULT_MEASURE = "Adjusted NAV";
export const DEFAULT_CHART_MEASURE = `calc-field-${DEFAULT_MEASURE}`;
export const DEFAULT_CHARACTERISTIC = "assetClass";
export const EMPTY_CARD_FILTERS = { investors: [], assetClasses: [] };

export function buildChartConfig(): ChartCardConfig {
  return {
    type: "chart",
    chartType: "pie" as ChartType,
    pieDimension: "assetClass",
    graphDataFormation: {
      default: { measure: DEFAULT_CHART_MEASURE, groupBy: DEFAULT_CHARACTERISTIC, chartType: "pie" as ChartType }
    },
    cardFilters: { ...EMPTY_CARD_FILTERS }
  };
}

export function buildKpiConfig(): KpiCardConfig {
  return {
    type: "kpi",
    metrics: ["totalNAV", "irr", "moic", "dpi"],
    cubics: { cubic1: { title: DEFAULT_MEASURE, filter: DEFAULT_MEASURE } },
    cardFilters: { ...EMPTY_CARD_FILTERS }
  };
}

export function buildSummaryConfig(): SummaryCardConfig {
  return {
    type: "summary",
    parameter: DEFAULT_MEASURE,
    cardFilters: { ...EMPTY_CARD_FILTERS }
  };
}

export function buildTableConfig(): TableCardConfig {
  return {
    type: "table",
    visibleColumns: ["name", "assetClass", "nav", "irr", "moic", "dpi"],
    columns: [{ aggregation: DEFAULT_MEASURE }],
    groupBy: [[DEFAULT_CHARACTERISTIC], ["deal"]],
    rawGroupBy: [DEFAULT_CHARACTERISTIC, "none"],
    rowBy: "deal",
    measures: [],
    calculatedFields: [DEFAULT_MEASURE],
    cardFilters: { ...EMPTY_CARD_FILTERS }
  };
}

function uid(): string {
  return `card-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function card(
  type: CardType,
  gridW: number,
  lastPreset: SizePreset | null,
  config: CanvasCard["config"],
  page: number,
  gridX = 0,
  gridY = 0,
  gridH?: number
): CanvasCard {
  const h = gridH ?? DEFAULT_GRID_H[type];
  return {
    id: uid(),
    type,
    gridX,
    gridY,
    gridW,
    gridH: h,
    widthPercent: gridWToWidthPercent(gridW),
    lastPreset,
    rowIndex: 0,
    orderInRow: 0,
    config,
    page
  };
}

export function createDefaultCardMock(type: CardType, page = 0): CanvasCard {
  switch (type) {
    case "chart":
      return card(type, PRESET_GRID_W.M, "M", buildChartConfig(), page);
    case "kpi":
      return card(type, PRESET_GRID_W.M, "M", buildKpiConfig(), page);
    case "summary":
      return card(type, PRESET_GRID_W.M, "M", buildSummaryConfig(), page);
    case "table":
      return card(type, PRESET_GRID_W.F, "F", buildTableConfig(), page);
    case "text":
      return card(type, PRESET_GRID_W.M, "M", { type: "text", content: "" }, page);
    case "ai_card":
      return card(type, PRESET_GRID_W.M, "M", { type: "ai_card", question: "", answer: "" }, page);
    case "exposure":
      return card(
        type,
        PRESET_GRID_W.M,
        "M",
        { type: "exposure", dimension: "assetClass", chartType: "donut", measure: "exposure" },
        page
      );
    case "cashflow":
      return card(type, PRESET_GRID_W.M, "M", { type: "cashflow" }, page);
    case "unfunded":
      return card(type, PRESET_GRID_W.M, "M", { type: "unfunded" }, page);
    case "performance":
      return card(type, PRESET_GRID_W.M, "M", { type: "performance", sortBy: "irr", topN: 3 }, page);
    case "benchmark":
      return card(type, PRESET_GRID_W.M, "M", { type: "benchmark", benchmarkIds: ["^SPX"] }, page);
    case "dataFreshness":
      return card(type, PRESET_GRID_W.S, "S", { type: "dataFreshness" }, page);
    case "macroBrief":
      return card(type, PRESET_GRID_W.F, "F", { type: "macroBrief", indicators: [] }, page);
  }
}
